
import { Box, Button, FormControl, OutlinedInput, Typography } from '@mui/material';

import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { AppRadioGroup, AppRadioGroupProps } from 'components/core/AppRadioGroup/AppRadioGroup';
import Grid from 'components/core/Grid';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddIcon from '@mui/icons-material/Add';
import { useLoginState } from 'components/views/pages/_general/useLoginState';

import styled from 'styled-components';

import { useState, useEffect, useRef } from 'react';
import {
  useGuestOfferCodeMutation,
  usePublicOffersQuery,
  getOfferValueDisplay,
  type OfferMutationResponse,
} from "../../hooks/useOfferQueries";
import { useCheckoutParams } from '../../hooks/useCheckoutParams';
import { useRouter } from 'next/router';
import { CheckoutParamEnum } from 'components/views/pages/Checkout/types';
import { type Offer, useToggle } from '@alluradirect/react';
import { CheckoutUserFlowEnum } from 'components/views/pages/Checkout/types';
import { CheckoutLoginCreateAccountForm, CheckoutUserFlowModal } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutLoginCreateAccountForm';
import { CheckoutBoxWrapper } from '../../styles';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';

export const SecureGrid = styled(Grid)`
  display: flex;
  align-items: center;

  & svg {
    margin-right: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.palette.primary.main};
  }
`;

export const StyledOutlinedInput = styled(OutlinedInput)`
background-color: #fff;
`;

export const AccountButton = styled(Button)`
padding: 0px ${props => props.theme.spacing(0.5)};
margin: 0px;
min-width: auto;
font-size: 0.8rem;
`;


export const PromoResponse = styled(Typography)`
display: flex;
align-items: center;
font-weight: 500;

& svg {
  margin-right: ${props => props.theme.spacing(1)};
  font-size: 1rem;
}

animation: fadeInOfferApplied 6s ease forwards;  

@keyframes fadeInOfferApplied {
  0% {
    opacity: 0;
    transform: translateX(-10px);    
  }
  20% {
    opacity: 1;
    transform: translateX(0px);  
  }
  90% {        
    opacity: 1;
    height: auto;
  }
  100% {
    opacity: 0;    
    height: 0;
    overflow: hidden;
  }
}
`;

export const PromoTypography = styled(Typography)`
display: block;
text-align: center;
margin-top: ${props => props.theme.spacing(1)};

& svg {
  margin-right: ${props => props.theme.spacing(1)};
  font-size: 1rem;
}
`;


interface Props {
  isLoading: boolean;
  loginState: string;
}

type OfferButton = AppRadioGroupProps['buttons'][0] & { id: number };

const getOfferButton = (offer: Offer): OfferButton => ({
  title: offer ? offer.TITLE : '',
  subtitle: offer ? offer.DESCRIPTION : '',
  subtitle2: getOfferValueDisplay(offer),
  value: offer ? offer.CODE : '',
  id: offer ? offer.OFFER_ID : 0,
});

export const CheckoutPromoDetails = ({ isLoading, loginState }: Props) => {
  const [promoCode, setPromoCode] = useState('');
  const [queryError, setQueryError] = useState<string | null>(null);
  const [ownerOffers, setOwnerOffers] = useState<OfferButton[]>([]);
  const [alluraOffers, setAlluraOffers] = useState<OfferButton[]>([]);
  const [ownerGiftCertificates, setOwnerGiftCertificates] = useState<OfferButton[]>([]);
  const [alluraGiftCertificates, setAlluraGiftCertificates] = useState<OfferButton[]>([]);
  const [checkoutUserFlowStep, setCheckoutUserFlowStep] = useState(CheckoutUserFlowEnum.Login);


  const codeList = useRef<string[]>([]);
  const { propertyId, owner_offer_code, allura_offer_code, owner_gift_certificate_code, allura_gift_certificate_code } = useCheckoutParams();
  const { userId } = useLoginState({ propertyId });
  const router = useRouter();
  const dialogToggle = useToggle();

  useEffect(() => {
    setPromoCode('');
    setQueryError(null);
  }, [userId]);

  const mutation = useGuestOfferCodeMutation({});

  const handleAddCodeList = (code: string) => {
    if (codeList.current.includes(code)) return;
    codeList.current.push(code);
  }

  const handleGetOfferFromCode = async (code: string) => {
    const { DATA: { OFFERS, GIFT_CERTIFICATES } } = await mutation.mutateAsync({
      urlArgs: [code]
    });
    return { OFFERS, GIFT_CERTIFICATES };
  }

  usePublicOffersQuery({
    onSuccess: (data) => {
      const { OFFERS, GIFT_CERTIFICATES } = data?.DATA || {};
      const ownerOffers = OFFERS?.OWNER?.filter(o => o)?.map(getOfferButton) ?? [];
      const alluraOffers = OFFERS?.ALLURA?.filter(o => o)?.map(getOfferButton) ?? [];
      const ownerGiftCertificates = GIFT_CERTIFICATES?.OWNER?.filter(o => o)?.map(getOfferButton) ?? [];
      const alluraGiftCertificates = GIFT_CERTIFICATES?.ALLURA?.filter(o => o)?.map(getOfferButton) ?? [];
      codeList.current =
        codeList.current.filter(c =>
          ownerOffers.findIndex(o => o.value === c) === -1
          &&
          alluraOffers.findIndex(o => o.value === c) === -1
          &&
          ownerGiftCertificates.findIndex(o => o.value === c) === -1
          &&
          alluraGiftCertificates.findIndex(o => o.value === c) === -1
        );
      if (owner_offer_code && !(codeList.current.includes(owner_offer_code) || codeList.current.includes(owner_offer_code.toLowerCase()))) codeList.current.push(owner_offer_code);
      if (allura_offer_code && !(codeList.current.includes(allura_offer_code) || codeList.current.includes(allura_offer_code.toLowerCase()))) codeList.current.push(allura_offer_code);
      if (owner_gift_certificate_code && !(codeList.current.includes(owner_gift_certificate_code) || codeList.current.includes(owner_gift_certificate_code.toLowerCase()))) codeList.current.push(owner_gift_certificate_code);
      if (allura_gift_certificate_code && !(codeList.current.includes(allura_gift_certificate_code) || codeList.current.includes(allura_gift_certificate_code.toLowerCase()))) codeList.current.push(allura_gift_certificate_code);
      Promise.allSettled(codeList.current.map(handleGetOfferFromCode))
        .then((results) => {
          const offers = results.filter((r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof handleGetOfferFromCode>>> => r.status === 'fulfilled').map(r => r.value);
          const codeToUpdate = results.map((r, i) => r.status === 'fulfilled' ? codeList.current[i] : "").filter(c => c);
          const ownerOffersToAdd = offers.filter(o => o?.OFFERS.OWNER).map(o => getOfferButton(o.OFFERS.OWNER)).filter(o => ownerOffers.findIndex(v => v.id === o.id) === -1);
          const alluraOffersToAdd = offers.filter(o => o?.OFFERS.ALLURA).map(o => getOfferButton(o.OFFERS.ALLURA)).filter(o => alluraOffers.findIndex(v => v.id === o.id) === -1);
          const ownerGiftCertificatesToAdd = offers.filter(o => o?.GIFT_CERTIFICATES.OWNER).map(o => getOfferButton(o.GIFT_CERTIFICATES.OWNER)).filter(o => ownerGiftCertificates.findIndex(v => v.id === o.id) === -1);
          const alluraGiftCertificatesToAdd = offers.filter(o => o?.GIFT_CERTIFICATES.ALLURA).map(o => getOfferButton(o.GIFT_CERTIFICATES.ALLURA)).filter(o => alluraGiftCertificates.findIndex(v => v.id === o.id) === -1);

          codeList.current = codeToUpdate;
          const ownerOffersList = [...ownerOffers, ...ownerOffersToAdd]
          const alluraOffersList = [...alluraOffers, ...alluraOffersToAdd]
          const ownerGiftCertificatesList = [...ownerGiftCertificates, ...ownerGiftCertificatesToAdd]
          const alluraGiftCertificatesList = [...alluraGiftCertificates, ...alluraGiftCertificatesToAdd]
          setOwnerOffers(ownerOffersList);
          setAlluraOffers(alluraOffersList);
          setOwnerGiftCertificates(ownerGiftCertificatesList);
          setAlluraGiftCertificates(alluraGiftCertificatesList);

          let ownerOffer, alluraOffer, ownerGiftCertificate, alluraGiftCertificate;
          if (owner_offer_code) {
            ownerOffer = ownerOffersList.find(o => o.value === owner_offer_code);
          } else if (ownerOffersList.length > 0) {
            ownerOffer = ownerOffersList[0];
          }
          if (allura_offer_code) {
            alluraOffer = alluraOffersList.find(o => o.value === allura_offer_code);
          } else if (alluraOffersList.length > 0) {
            alluraOffer = alluraOffersList[0];
          }
          if (owner_gift_certificate_code) {
            ownerGiftCertificate = ownerGiftCertificatesList.find(o => o.value === owner_gift_certificate_code);
          }
          if (allura_gift_certificate_code) {
            alluraGiftCertificate = alluraGiftCertificatesList.find(o => o.value === allura_gift_certificate_code);
          }
          if (ownerGiftCertificate && alluraGiftCertificate) {
            handleApplyBothGiftCertificatesUrlParams(ownerGiftCertificate, alluraGiftCertificate);
          } else if (ownerGiftCertificate) {
            handleApplyGiftCertificateUrlParams(ownerGiftCertificate, false);
          } else if (alluraGiftCertificate) {
            handleApplyGiftCertificateUrlParams(alluraGiftCertificate, true);

          }
          if (ownerOffer && alluraOffer) {
            handleApplyBothOffersUrlParams(ownerOffer, alluraOffer);
          } else if (ownerOffer) {
            handleApplyOfferUrlParams(ownerOffer, false);
          } else if (alluraOffer) {
            handleApplyOfferUrlParams(alluraOffer, true);
          }
        })
    }
  });

  const handleApplyGiftCertificateUrlParams = (offer: OfferButton | undefined, isAllura: boolean) => {
    const updateQuery = isAllura ? {
      [CheckoutParamEnum.allura_gift_certificate_code]: offer?.value,
      [CheckoutParamEnum.allura_gift_certificate_id]: offer?.id,
    } : {
      [CheckoutParamEnum.owner_gift_certificate_code]: offer?.value,
      [CheckoutParamEnum.owner_gift_certificate_id]: offer?.id,
    };
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          ...updateQuery,
        },
      },
      undefined,
      {
        shallow: false,
        scroll: false,
      },
    );
  }


  const handleApplyOfferUrlParams = (offer: OfferButton | undefined, isAllura: boolean) => {
    const updateQuery = isAllura ? {
      [CheckoutParamEnum.allura_offer_code]: offer?.value,
      [CheckoutParamEnum.allura_offer_id]: offer?.id,
    } : {
      [CheckoutParamEnum.owner_offer_code]: offer?.value,
      [CheckoutParamEnum.owner_offer_id]: offer?.id,
    };
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          ...updateQuery,
        },
      },
      undefined,
      {
        shallow: false,
        scroll: false,
      },
    );
  }

  const handleApplyBothOffersUrlParams = (ownerOffer: OfferButton | undefined, alluraOffer: OfferButton | undefined) => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          [CheckoutParamEnum.owner_offer_code]: ownerOffer?.value,
          [CheckoutParamEnum.owner_offer_id]: ownerOffer?.id,
          [CheckoutParamEnum.allura_offer_code]: alluraOffer?.value,
          [CheckoutParamEnum.allura_offer_id]: alluraOffer?.id,
        },
      },
      undefined,
      {
        shallow: false,
        scroll: false,
      },
    );
  }

  const handleApplyBothGiftCertificatesUrlParams = (ownerGiftCertificate: OfferButton | undefined, alluraGiftCertificate: OfferButton | undefined) => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          [CheckoutParamEnum.owner_gift_certificate_code]: ownerGiftCertificate?.value,
          [CheckoutParamEnum.owner_gift_certificate_id]: ownerGiftCertificate?.id,
          [CheckoutParamEnum.allura_gift_certificate_code]: alluraGiftCertificate?.value,
          [CheckoutParamEnum.allura_gift_certificate_id]: alluraGiftCertificate?.id,
        },
      },
      undefined,
      {
        shallow: false,
        scroll: false,
      },
    );
  }


  const handleApplyGiftCertificate = (isAllura: boolean) => (codeOrOffer: any) => {
    const offer = typeof codeOrOffer === 'string' ?
      (isAllura ? alluraGiftCertificates : ownerGiftCertificates).find(o => o.value === codeOrOffer)
      : codeOrOffer;
    if (!offer) return;

    handleApplyGiftCertificateUrlParams(offer, isAllura);
  }

  const handleApplyOffer = (isAllura: boolean) => (codeOrOffer: any) => {
    const offer = typeof codeOrOffer === 'string' ?
      (isAllura ? alluraOffers : ownerOffers).find(o => o.value === codeOrOffer)
      : codeOrOffer;
    if (!offer) return;

    handleApplyOfferUrlParams(offer, isAllura);
  }

  const handlePromoCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQueryError(null);
    setPromoCode(event.target.value);
  };

  const handleCodeQuery = async () => {
    try {
      const OFFERS = await handleGetOfferFromCode(promoCode);
      let ownerUsed, alluraUsed, ownerGiftCertificateUsed, alluraGiftCertificateUsed;
      if (OFFERS.OFFERS.OWNER) {
        const offer = OFFERS.OFFERS.OWNER;
        const OfferButton = getOfferButton(offer);
        setOwnerOffers(offers => {
          if (offers.findIndex(o => o.value === offer.CODE) !== -1) return offers;
          ownerUsed = true;
          return [...offers, OfferButton]
        })
        // handleApplyOffer(false)(OfferButton);
      }
      if (OFFERS.OFFERS.ALLURA) {
        const offer = OFFERS.OFFERS.ALLURA;
        const OfferButton = getOfferButton(offer);
        setAlluraOffers(offers => {
          if (offers.findIndex(o => o.value === offer.CODE) !== -1) return offers;
          alluraUsed = true;
          return [...offers, OfferButton]
        })
        // handleApplyOffer(true)(OfferButton);
      }

      if (OFFERS.GIFT_CERTIFICATES.OWNER) {
        const offer = OFFERS.GIFT_CERTIFICATES.OWNER;
        const OfferButton = getOfferButton(offer);
        setOwnerGiftCertificates(offers => {
          if (offers.findIndex(o => o.value === offer.CODE) !== -1) return offers;
          ownerGiftCertificateUsed = true;
          return [...offers, OfferButton]
        })
        // handleApplyOffer(false)(OfferButton);

      }
      if (OFFERS.GIFT_CERTIFICATES.ALLURA) {
        const offer = OFFERS.GIFT_CERTIFICATES.ALLURA;
        const OfferButton = getOfferButton(offer);
        setAlluraGiftCertificates(offers => {
          if (offers.findIndex(o => o.value === offer.CODE) !== -1) return offers;
          alluraGiftCertificateUsed = true;
          return [...offers, OfferButton]
        })
        // handleApplyOffer(true)(OfferButton);
      }

      if (OFFERS.OFFERS.OWNER && OFFERS.OFFERS.ALLURA) {
        handleApplyBothOffersUrlParams(getOfferButton(OFFERS.OFFERS.OWNER), getOfferButton(OFFERS.OFFERS.ALLURA));
      } else if (OFFERS.OFFERS.OWNER) {
        handleApplyOffer(false)(getOfferButton(OFFERS.OFFERS.OWNER));
      } else if (OFFERS.OFFERS.ALLURA) {
        handleApplyOffer(true)(getOfferButton(OFFERS.OFFERS.ALLURA));
      }
      if ((OFFERS.OFFERS.OWNER && !ownerUsed) && (OFFERS.OFFERS.ALLURA && !alluraUsed)) {
        setQueryError("Code has already been used.");
      } else {
        handleAddCodeList(promoCode)
        setQueryError("");
        setPromoCode("");
      }

      if (OFFERS.GIFT_CERTIFICATES.OWNER && OFFERS.GIFT_CERTIFICATES.ALLURA) {
        handleApplyBothGiftCertificatesUrlParams(getOfferButton(OFFERS.GIFT_CERTIFICATES.OWNER), getOfferButton(OFFERS.GIFT_CERTIFICATES.ALLURA));
      } else if (OFFERS.GIFT_CERTIFICATES.OWNER) {
        handleApplyGiftCertificate(false)(getOfferButton(OFFERS.GIFT_CERTIFICATES.OWNER));
      } else if (OFFERS.GIFT_CERTIFICATES.ALLURA) {
        handleApplyGiftCertificate(true)(getOfferButton(OFFERS.GIFT_CERTIFICATES.ALLURA));
      }
      if ((OFFERS.GIFT_CERTIFICATES.OWNER && !ownerGiftCertificateUsed) && (OFFERS.GIFT_CERTIFICATES.ALLURA && !alluraGiftCertificateUsed)) {
        setQueryError("Code has already been used.");
      } else {
        handleAddCodeList(promoCode)
        setQueryError("");
        setPromoCode("");
      }


    } catch (error: any) {
      if (error?.response?.status === 401) {
        dialogToggle.open();
      }
      if (typeof error?.message === 'string') {
        const data = JSON.parse(error.message) as OfferMutationResponse;
        setQueryError(data?.ERRORS?.CLIENT_MESSAGE ?? "Something went wrong.");
      }
    }
  }

  const hasOffersAvailable = ownerOffers?.length > 0 || alluraOffers?.length > 0 || ownerGiftCertificates?.length > 0 || alluraGiftCertificates?.length > 0;
  const { isXs } = useScreenWidth();
  const { isOpen, close, open } = useToggle();

  return (
    <>
      <Grid item xs={12}>
        <AppDivider marginVertical="16px" borderWidth="0" />
      </Grid>

      <CheckoutBoxWrapper>
        <Box width={'100%'}>
          <LoadingSkeleton isLoading={isLoading} >
            <Box>
              <FormControl variant="outlined" fullWidth size="small">
                <StyledOutlinedInput
                  type="text"
                  placeholder='Apply a promotion offer'
                  value={promoCode}
                  onChange={handlePromoCodeChange}
                  endAdornment={
                    <Button color="secondary" variant="text" startIcon={<AddIcon />} onClick={handleCodeQuery} >Apply</Button>
                  }
                />
              </FormControl>
              {queryError !== null && (
                queryError ? (
                  <PromoResponse variant="body2" color="error" mt={1}>
                    <WarningIcon color="error" /> {queryError}
                  </PromoResponse>
                ) : (
                  <PromoResponse variant="body2" mt={1}>
                    <CheckIcon color="success" /> Code is valid and has been applied.
                  </PromoResponse>
                )
              )}
              {loginState === 'notloggedin' ? (
                <PromoTypography variant="body2">
                  <AccountButton variant="text" color='secondary' onClick={open}>Log in</AccountButton>or<AccountButton variant="text" color='secondary' onClick={open}>create an account</AccountButton>to view all available offers.</PromoTypography>
              ) : null}
            </Box>
          </LoadingSkeleton>
          {hasOffersAvailable ? (
            <Grid item xs={12}>
              <AppDivider marginVertical="8px" borderWidth='0' />
            </Grid>
          ) : null}
          <Box sx={hasOffersAvailable ? null : { display: "none" }} >
            <Grid item xs={12}>
              {!isLoading ? (
                <>
                  {ownerOffers?.length > 0 && <>
                    <LoadingSkeleton isLoading={isLoading}>
                      <AppDivider marginVertical="16px" borderWidth='1' />
                      <Typography variant="h6" alignItems="center" display="flex"><TaskAltIcon color="success" sx={{ marginRight: '8px' }} />Offer available...</Typography>
                      <Typography mb={2}>Select an offer to apply to your reservation.</Typography>
                      <AppRadioGroup
                        buttons={ownerOffers}
                        value={owner_offer_code ?? ""}
                        setValue={handleApplyOffer(false)}
                        titleStyle={{ fontSize: '16px' }}
                        subtitle2Style={{ fontWeight: '500', color: 'green' }}
                        color="inherit"
                        disabled={isLoading}
                        isSingleColumn
                      />
                    </LoadingSkeleton>
                  </>}
                  {ownerOffers?.length > 0 && alluraOffers?.length > 0 && <>
                    <AppDivider marginVertical="24px" borderWidth='1' />
                    <Typography variant="h6" alignItems="center" display="flex"><TaskAltIcon color="success" sx={{ marginRight: '8px' }} />Bonus offer available...</Typography>
                    <Typography mb={2}>Select an offer to apply to your reservation.</Typography>
                  </>
                  }
                  {alluraOffers?.length > 0 && <>
                    <LoadingSkeleton isLoading={isLoading}>
                      {ownerOffers?.length == 0 &&
                        <>
                          <AppDivider marginVertical="16px" borderWidth='1' />
                          <Typography variant="h6" alignItems="center" display="flex"><TaskAltIcon color="success" sx={{ marginRight: '8px' }} />Offer available...</Typography>
                          <Typography mb={2}>Select an offer to apply to your reservation.</Typography>
                        </>
                      }
                      <AppRadioGroup
                        buttons={alluraOffers}
                        value={allura_offer_code ?? ""}
                        setValue={handleApplyOffer(true)}
                        titleStyle={{ fontSize: '16px' }}
                        subtitle2Style={{ fontWeight: '500', color: 'green' }}
                        color="inherit"
                        disabled={isLoading}
                        isSingleColumn
                      />
                    </LoadingSkeleton>
                  </>}
                  {ownerGiftCertificates?.length > 0 && <>
                    <LoadingSkeleton isLoading={isLoading}>
                      {ownerOffers?.length == 0 && alluraOffers?.length == 0 &&
                        <>
                          <AppDivider marginVertical="16px" borderWidth='1' />
                          <Typography variant="h6" alignItems="center" display="flex"><TaskAltIcon color="success" sx={{ marginRight: '8px' }} />Gift available...</Typography>
                          <Typography mb={2}>Select a gift to apply to your reservation.</Typography>
                        </>
                      }
                      <AppRadioGroup
                        buttons={ownerGiftCertificates}
                        value={owner_gift_certificate_code ?? ""}
                        setValue={handleApplyGiftCertificate(false)}
                        titleStyle={{ fontSize: '16px' }}
                        subtitle2Style={{ fontWeight: '500', color: 'green' }}
                        color="inherit"
                        disabled={isLoading}
                        isSingleColumn
                      />
                    </LoadingSkeleton>
                  </>}
                  {alluraGiftCertificates?.length > 0 && <>
                    <LoadingSkeleton isLoading={isLoading}>
                      {ownerOffers?.length == 0 && alluraOffers?.length == 0 &&
                        <>
                          <AppDivider marginVertical="16px" borderWidth='1' />
                          <Typography variant="h6" alignItems="center" display="flex"><TaskAltIcon color="success" sx={{ marginRight: '8px' }} />Gift available...</Typography>
                          <Typography mb={2}>Select a gift to apply to your reservation.</Typography>
                        </>
                      }
                      <AppRadioGroup
                        buttons={alluraGiftCertificates}
                        value={allura_gift_certificate_code ?? ""}
                        setValue={handleApplyGiftCertificate(true)}
                        titleStyle={{ fontSize: '16px' }}
                        subtitle2Style={{ fontWeight: '500', color: 'green' }}
                        color="inherit"
                        disabled={isLoading}
                        isSingleColumn
                      />
                    </LoadingSkeleton>
                  </>}
                </>
              ) : null}
            </Grid>
            <CheckoutUserFlowModal
              dialogToggle={dialogToggle}
              checkoutUserFlowStep={checkoutUserFlowStep}
              setCheckoutUserFlowStep={setCheckoutUserFlowStep}
              checkoutLoginEmail=""
            />
          </Box>
        </Box >
      </CheckoutBoxWrapper>


      <ConfirmationDialog
        dialogTitle=""
        handleCloseDialog={close}
        isDialogOpen={isOpen}
        isLoading={isLoading}
        hasBottomBar={false}
        hasHeaderBar={isXs ? true : false}
        maxWidth={'xs'}
      >
        <CheckoutLoginCreateAccountForm isLoading={false} onClose={close} />
      </ConfirmationDialog>
    </>
  );
};
